import { useEffect, useState } from "react";
import { lazy, Suspense } from "react";
import { MotionConfig } from "framer-motion";
import "./App.css";
export const BUILD_VERSION = "v56";
import "./styles/mobileOptimization.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

// Import Firebase Components and Context
import { AuthProvider } from "./contexts/AuthContext";
import { AdminProvider } from "./contexts/AdminContext";
import FingerprintAdminAccess from "./components/FingerprintAdminAccess";
import CookieBanner from "./components/CookieBanner";

// Lazy: below-the-fold sections + admin panel (2700+ lines) stay out of the main bundle
const FirebaseDonationSection = lazy(() => import("./components/FirebaseDonationSection"));
const PublicRoadmap = lazy(() => import("./components/PublicRoadmap"));
const AdminPanel = lazy(() => import("./components/AdminPanelCanonical"));

// Import critical components (needed immediately)
import Hero from "./components/Hero";
import FixedSideButtons from "./components/FixedSideButtons";
import PriceTicker from "./components/PriceTicker";
import StickyNavigation from "./components/StickyNavigation";
import CoinFlipBackground from "./components/CoinFlipBackground";
import StarfieldBackground from "./components/StarfieldBackground";
import Card3DEffect from "./components/Card3DEffect";
import MobileBottomNav from "./components/MobileBottomNav";
import MobileInstallPrompt from "./components/MobileInstallPrompt";
import VIBIEMoon from "./components/VIBIEMoon";
// VibieChat removed as requested

// Lazy load non-critical components
const AboutSection = lazy(() => import("./components/AboutSection"));
const TeamSection = lazy(() => import("./components/TeamSection"));
const CallToAction = lazy(() => import("./components/CallToAction"));
// Description (Why Choose SOLVIBE) removed per user request
const Tokenomics = lazy(() => import("./components/Tokenomics"));
import PriceChart from "./components/PriceChart"; // Direct import to fix lazy loading issue
const WhereToBuy = lazy(() => import("./components/WhereToBuy"));
const SwapSection = lazy(() => import("./components/SwapSection"));
const LiquidityPool = lazy(() => import("./components/LiquidityPool"));
const ContractAddress = lazy(() => import("./components/ContractAddress"));
const SocialLinks = lazy(() => import("./components/SocialLinks"));
const FAQ = lazy(() => import("./components/FAQ"));
const VibieAIModSection = lazy(() => import("./components/VibieAIModSection"));
const Footer = lazy(() => import("./components/Footer"));

// Loading component — dark background prevents white flash when Suspense triggers
const LoadingSpinner = () => (
  <div
    className="flex items-center justify-center"
    style={{ minHeight: '420px', background: '#0a0a12' }}
  >
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-solana-primary" />
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Small helper component: redirect pretty paths like /whitepaper to the root with a hash and scroll
function HashRedirect({ target }) {
  const navigate = useNavigate();
  useEffect(() => {
    // Store the scroll target so Home can pick it up after navigation
    sessionStorage.setItem('svb_scroll_target', target);
    navigate('/', { replace: true });
  }, [target, navigate]);
  return null;
}

// Helper for static HTML redirects (e.g., /brand-assets -> /brand-assets.html)
function StaticRedirect({ to }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}

const Home = () => {
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  // Mobile detection for performance optimization
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobile);
      
      // Add mobile class for CSS optimizations
      if (mobile) {
        document.body.classList.add('mobile-device');
        document.body.classList.add('mobile-optimized');
      } else {
        document.body.classList.remove('mobile-device');
        document.body.classList.remove('mobile-optimized');
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Mark page as loaded after initial render for animation optimization
    const loadTimer = setTimeout(() => {
      document.body.classList.add('loaded');
    }, 100);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      clearTimeout(loadTimer);
    };
  }, []);

  const helloWorldApi = async () => {
    try {
      await axios.get(`${API}/`);
    } catch (e) {
      // Silent error handling for production
    }
  };

  useEffect(() => {
    helloWorldApi();
  }, []);

  // Scroll to anchor when the hash is present (e.g., /#tokenomics)
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        // Use a slight delay to ensure lazy components are mounted
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
      }
    }
  }, [location]);

  // Handle direct URL deep-links: /chart → #chart, /faq → #faq, etc.
  // HashRedirect stores the target in sessionStorage before navigating to "/".
  // We retry with increasing delays so lazy-loaded sections have time to mount
  // before we attempt scrollIntoView — this prevents the "jump then scroll away" bug.
  useEffect(() => {
    const target = sessionStorage.getItem('svb_scroll_target');
    if (!target) return;
    sessionStorage.removeItem('svb_scroll_target'); // consume immediately

    let scrolled = false;

    const tryScroll = () => {
      if (scrolled) return;
      const el = document.getElementById(target);
      if (el) {
        scrolled = true;
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    // 200 ms  — sections already in DOM (non-lazy like #chart, #home)
    // 500 ms  — most lazy sections mounted by now
    // 950 ms  — slow network / heavy sections (faq, liquidity, swap)
    // 1600 ms — last-resort fallback for very slow connections
    const timers = [200, 500, 950, 1600].map(d => setTimeout(tryScroll, d));
    return () => {
      scrolled = true; // prevent scroll after unmount
      timers.forEach(clearTimeout);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    // MotionConfig: on mobile, reducedMotion="always" forces all framer-motion
    // animations to near-zero duration. This is the single most impactful change
    // for Android scroll performance — it eliminates GPU compositor layer buildup
    // from whileInView animations across every section.
    <MotionConfig reducedMotion={isMobile ? "always" : "never"}>
    <div className="min-h-screen bg-solana-dark text-white mobile-page-content">
      {/* Coin Flip Background Animation */}
      <CoinFlipBackground />

      {/* Deep-space starfield background (dense, subtle, performance-aware) */}
      <StarfieldBackground />

      {/* Global subtle 3D tilt + glass effect on all cards */}
      <Card3DEffect />
      
      {/* Price Ticker - Very Top */}
      <PriceTicker />
      
      {/* Sticky Navigation - Directly below ticker */}
      <StickyNavigation />
      
      {/* Fixed Swap Button */}
      <FixedSideButtons />
      
      {/* Main landmark (a11y) — wraps all page content sections */}
      <main>

      {/* 1. Hero Section with Integrated Live Trading */}
      <div id="home">
        <Hero />
      </div>

      {/* 2. About Section - What is SOLVIBE? */}
      <Suspense fallback={<LoadingSpinner />}>
        <div id="about" className="scroll-mt-24">
          <AboutSection />
        </div>
      </Suspense>

      {/* 3. Tokenomics Stats */}
      <Suspense fallback={<LoadingSpinner />}>
        <div id="tokenomics" className="scroll-mt-24">
          <Tokenomics />
        </div>
      </Suspense>
      
      {/* 5. Social Media Icons Section */}
      <Suspense fallback={<LoadingSpinner />}>
        <div id="community" className="scroll-mt-24">
          <SocialLinks />
        </div>
      </Suspense>
      
      {/* 5.5. Vibie AI Mod Section - NEW POSITION */}
      <Suspense fallback={<LoadingSpinner />}>
        <div id="vibie-mod" className="scroll-mt-24">
          <VibieAIModSection />
        </div>
      </Suspense>
      
      {/* 6. How to Access $SVB Section */}
      <Suspense fallback={<LoadingSpinner />}>
        <div id="buy-trade" className="scroll-mt-24">
          <WhereToBuy />
        </div>
      </Suspense>
      
      {/* 6.5. Community & CTA Section */}
      <Suspense fallback={<LoadingSpinner />}>
        <div id="cta" className="scroll-mt-24">
          <CallToAction />
        </div>
      </Suspense>

      {/* 7. Support SOLVIBE - Firebase Donation Section with Dynamic Goals */}
      <Suspense fallback={<LoadingSpinner />}>
        <div id="donations" className="scroll-mt-24">
          <FirebaseDonationSection />
        </div>
      </Suspense>
      
      {/* 8. Public Roadmap Section */}
      <Suspense fallback={<LoadingSpinner />}>
        <div id="roadmap" className="scroll-mt-24">
          <PublicRoadmap />
        </div>
      </Suspense>
      
      {/* 9. Contract Address & Verification */}
      <Suspense fallback={<LoadingSpinner />}>
        <div id="contract" className="scroll-mt-24">
          <ContractAddress />
        </div>
      </Suspense>
      
      {/* Additional sections for completeness */}
      <div id="chart" className="scroll-mt-24">
        <PriceChart />
      </div>
      
      <Suspense fallback={<LoadingSpinner />}>
        <div id="swap" className="scroll-mt-24">
          <SwapSection />
        </div>
      </Suspense>
      <Suspense fallback={<LoadingSpinner />}>
        <div id="liquidity" className="scroll-mt-24">
          <LiquidityPool />
        </div>
      </Suspense>
      <Suspense fallback={<LoadingSpinner />}>
        <div id="faq" className="scroll-mt-24">
          <FAQ />
        </div>
      </Suspense>

      {/* Team Section — after FAQ, cards always visible */}
      <Suspense fallback={<LoadingSpinner />}>
        <div id="team" className="scroll-mt-24">
          <TeamSection />
        </div>
      </Suspense>

      </main>

      {/* 11. Footer with Admin Access */}
      <div className="relative">
        <Suspense fallback={<LoadingSpinner />}>
          <Footer />
        </Suspense>
        
        {/* Admin Access - Positioned at same height as Privacy Policy, far left */}
        <div className="absolute inset-y-0 left-4 flex items-center z-20">
          <FingerprintAdminAccess onAdminAccess={() => {
            // This will be handled globally
            window.dispatchEvent(new CustomEvent('openAdminPanel'));
          }} />
        </div>
      </div>

      {/* Vibie AI Chatbot - Removed as requested */}

      {/* VIBIE Moon — decorative parallax background element */}
      <VIBIEMoon />


      {/* Mobile App Navigation (bottom bar, hidden on desktop) */}
      <MobileBottomNav />

      {/* PWA Install Prompt (mobile only, delayed) */}
      <MobileInstallPrompt />
    </div>
    </MotionConfig>
  );
};

function App() {
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  const handleAdminAccess = async () => {
    // Use AdminContext to handle login
    setShowAdminPanel(true);
  };

  const handleCloseAdminPanel = () => {
    setShowAdminPanel(false);
  };

  // Listen for custom event from FingerprintAdminAccess
  useEffect(() => {
    const handleOpenAdminPanel = () => {
      handleAdminAccess();
    };

    window.addEventListener('openAdminPanel', handleOpenAdminPanel);
    return () => {
      window.removeEventListener('openAdminPanel', handleOpenAdminPanel);
    };
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <AdminProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Home />} />

              {/* ── Section deep-links ─────────────────────────────────── */}
              {/* /home */}
              <Route path="/home"        element={<HashRedirect target="home" />} />
              {/* /about */}
              <Route path="/about"       element={<HashRedirect target="about" />} />
              <Route path="/about/*"     element={<HashRedirect target="about" />} />
              {/* /tokenomics */}
              <Route path="/tokenomics"  element={<HashRedirect target="tokenomics" />} />
              <Route path="/tokenomics/*"element={<HashRedirect target="tokenomics" />} />
              {/* /community */}
              <Route path="/community"   element={<HashRedirect target="community" />} />
              <Route path="/community/*" element={<HashRedirect target="community" />} />
              {/* /vibie */}
              <Route path="/vibie"       element={<HashRedirect target="vibie-mod" />} />
              <Route path="/vibie/*"     element={<HashRedirect target="vibie-mod" />} />
              {/* /buy */}
              <Route path="/buy"         element={<HashRedirect target="buy-trade" />} />
              <Route path="/buy/*"       element={<HashRedirect target="buy-trade" />} />
              {/* /roadmap */}
              <Route path="/roadmap"     element={<HashRedirect target="roadmap" />} />
              <Route path="/roadmap/*"   element={<HashRedirect target="roadmap" />} />
              {/* /contract */}
              <Route path="/contract"    element={<HashRedirect target="contract" />} />
              <Route path="/contract/*"  element={<HashRedirect target="contract" />} />
              {/* /chart */}
              <Route path="/chart"       element={<HashRedirect target="chart" />} />
              <Route path="/chart/*"     element={<HashRedirect target="chart" />} />
              {/* /swap */}
              <Route path="/swap"        element={<HashRedirect target="swap" />} />
              <Route path="/swap/*"      element={<HashRedirect target="swap" />} />
              {/* /liquidity */}
              <Route path="/liquidity"   element={<HashRedirect target="liquidity" />} />
              <Route path="/liquidity/*" element={<HashRedirect target="liquidity" />} />
              {/* /team */}
              <Route path="/team"        element={<HashRedirect target="team" />} />
              <Route path="/team/*"      element={<HashRedirect target="team" />} />
              {/* /faq */}
              <Route path="/faq"         element={<HashRedirect target="faq" />} />
              <Route path="/faq/*"       element={<HashRedirect target="faq" />} />
              {/* /donations */}
              <Route path="/donations"   element={<HashRedirect target="donations" />} />
              <Route path="/donations/*" element={<HashRedirect target="donations" />} />

              {/* ── Static file / page redirects ───────────────────────── */}
              {/* /whitepaper → opens PDF directly in browser */}
              <Route path="/whitepaper"       element={<StaticRedirect to="/whitepaper.pdf" />} />
              <Route path="/whitepaper/*"     element={<StaticRedirect to="/whitepaper.pdf" />} />
              {/* /brand-assets → static HTML page */}
              <Route path="/brand-assets"     element={<StaticRedirect to="/brand-assets.html" />} />
              <Route path="/brand-assets/*"   element={<StaticRedirect to="/brand-assets.html" />} />
              {/* /brand-pack → ZIP download */}
              <Route path="/brand-pack"       element={<StaticRedirect to="/SolVibe-Brand-Pack.zip" />} />
            </Routes>
          </BrowserRouter>
          
          {/* Admin Panel - Only shown when authenticated (lazy chunk loads on open) */}
          {showAdminPanel && (
            <Suspense fallback={null}>
              <AdminPanel onClose={handleCloseAdminPanel} />
            </Suspense>
          )}

          {/* GDPR Cookie Notice */}
          <CookieBanner />
        </AdminProvider>
      </AuthProvider>
    </div>
  );
}

export default App;